import { useState } from "react";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  useTheme,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Copyright } from "./components/BasePublic";
import { useLogin } from "./hooks/useLogin";

import BackgroundPadel from "../../images/fundoPadel.png";
import LogoApeak from "../../images/apeak_club.png";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { inviteToken: inviteTokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const inviteTokenFromQuery = searchParams.get("inviteToken");
  const inviteTokenFromPathFallback = (() => {
    const pathname = location?.pathname || "";
    const match = pathname.match(/^\/login\/([^/]+)\/?$/);
    return match?.[1] || null;
  })();
  const inviteToken =
    inviteTokenParam || inviteTokenFromQuery || inviteTokenFromPathFallback || null;
  const {
    handleSignIn,
    getErr,
    getErrMsg,
    successMessage,
    loading,
  } = useLogin();
  const theme = useTheme();

  const logoSrc = LogoApeak;
  const registerLink = inviteToken ? `/register/${inviteToken}` : "/register";

  const loginTheme = theme.custom?.login ?? {};

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: `linear-gradient(${loginTheme.overlay ?? "rgba(0, 0, 0, 0.55)"}, ${loginTheme.overlay ?? "rgba(0, 0, 0, 0.55)"}), url(${BackgroundPadel})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          borderRadius: 5,
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          border: `1px solid ${loginTheme.cardBorder ?? "rgba(255, 255, 255, 0.3)"}`,
          backdropFilter: "blur(5px)",
          bgcolor: loginTheme.cardBackground ?? "rgba(255, 255, 255, 0.88)",
          boxShadow: loginTheme.shadow ?? "0 22px 55px rgba(5, 15, 28, 0.35)",
        }}
      >
        <Stack spacing={1.2} alignItems="center" mb={2}>
          <Box
            component="img"
            src={logoSrc}
            alt="Logo Padel Rank"
            sx={{
              width: { xs: 120, sm: 180 },
              height: { xs: 120, sm: 180 },
              objectFit: "contain",
            }}
          />

          <Typography
            variant="body2"
            sx={{
              color: loginTheme.subtitle ?? "text.secondary",
              textAlign: "center",
              mb: 1,
            }}
          >
            Entre com seu email e senha
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleSignIn}>
          <TextField
            margin="normal"
            id="email"
            label="Email"
            name="email"
            variant="outlined"
            autoComplete="email"
            required
            fullWidth
            autoFocus
            error={getErr}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor:
                  loginTheme.inputBackground ?? "rgba(255, 255, 255, 0.92)",
              },
            }}
          />

          <TextField
            margin="normal"
            name="password"
            label="Senha"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            variant="outlined"
            fullWidth
            required
            error={getErr}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword((prev) => !prev)}
                    onMouseDown={(event) => event.preventDefault()}
                    aria-label={
                      showPassword ? "Ocultar senha" : "Mostrar senha"
                    }
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor:
                  loginTheme.inputBackground ?? "rgba(255, 255, 255, 0.92)",
              },
            }}
          />

          {getErrMsg ? (
            <Alert
              severity="error"
              variant="filled"
              sx={{
                mt: 1.5,
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontWeight: 600,
                py: 0.6,
              }}
            >
              {getErrMsg}
            </Alert>
          ) : null}

          {successMessage ? (
            <Alert
              severity="success"
              variant="filled"
              sx={{
                mt: 1.5,
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontWeight: 600,
                py: 0.6,
              }}
            >
              {successMessage}
            </Alert>
          ) : null}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              mb: 3,
              py: 1.2,
              borderRadius: 2.5,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: loginTheme.buttonText ?? "#f7fbff",
              bgcolor: "primary.main",
              boxShadow: "0 10px 30px rgba(24, 49, 83, 0.35)",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            {loading ? "Entrando..." : "Entrar"}
          </Button>

          <Button
            component={RouterLink}
            to={registerLink}
            fullWidth
            variant="outlined"
            sx={{
              mb: 1,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Cadastrar
          </Button>
        </Box>

        <Copyright sx={{ mt: 1 }} />
      </Box>
    </Box>
  );
}
